// Necessary for typing in actions and getters.
/* eslint-disable @typescript-eslint/no-use-before-define, no-use-before-define */

import { useFirebaseAuth, useFirebaseCloudFunctions, useFirestore } from '@/services';
import { defineModule, localActionContext } from 'direct-vuex';
import { ActionContext } from 'vuex';
import { moduleGetterContext } from '..';

export interface GroupState {
  groupId: string;
  linkId: string;
}

export const groupModule = defineModule({
  namespaced: true,
  state: (): GroupState => ({
    groupId: '',
    linkId: ''
  }),
  getters: {
    getGroupId: (...args): string => {
      const { state } = getterContext(args);
      return state.groupId;
    },
    getLinkId: (...args): string => {
      const { state } = getterContext(args);
      return state.linkId;
    }
  },
  mutations: {
    UPDATE_GROUP_ID: (state, payload: string) => {
      state.groupId = payload;
    },
    CLEAR_GROUP_ID: state => {
      state.groupId = '';
    },
    UPDATE_LINK_ID: (state, payload: string) => {
      state.linkId = payload;
    },
    CLEAR_LINK_ID: state => {
      state.groupId = '';
    }
  },
  actions: {
    createGroup: async (context, groupName: string): Promise<void> => {
      const { commit } = actionContext(context);
      const { createGroup } = useFirebaseCloudFunctions();
      const { auth } = useFirebaseAuth();
      const userIdToken = await auth.currentUser?.getIdToken(true);

      if (!userIdToken) return;

      const groupId = await createGroup(groupName, userIdToken);
      commit.UPDATE_GROUP_ID(groupId);
    },
    getGroupId: async (context): Promise<string> => {
      const { commit } = actionContext(context);
      const { getUserGroupId } = useFirestore();

      const groupId = (await getUserGroupId()) ?? '';

      commit.UPDATE_GROUP_ID(groupId);

      return groupId;
    },
    getLinkId: async (context): Promise<string> => {
      const { commit } = actionContext(context);
      const { getGroupLinkId } = useFirestore();

      const linkId = (await getGroupLinkId()) ?? '';

      commit.UPDATE_LINK_ID(linkId);

      return linkId;
    },
    getGroupNameByLinkId: (_, linkId: string): Promise<string> => {
      const { getGroupNameByLinkId } = useFirebaseCloudFunctions();

      return getGroupNameByLinkId(linkId);
    },
    joinWithGroupLink: async (_, linkId: string): Promise<boolean> => {
      const { joinWithGroupLink } = useFirebaseCloudFunctions();
      const { auth } = useFirebaseAuth();
      const userIdToken = await auth.currentUser?.getIdToken(true);

      if (!userIdToken) return Promise.reject();

      return joinWithGroupLink(userIdToken, linkId);
    },
    createNewGroupLink: async (context): Promise<string> => {
      const { dispatch, commit } = actionContext(context);
      const { createNewGroupLink } = useFirebaseCloudFunctions();

      const groupId = await dispatch.getGroupId();
      const linkId = await createNewGroupLink(groupId);
      commit.UPDATE_LINK_ID(linkId);

      return linkId;
    }
  }
});

const getterContext = (args: [unknown, unknown, unknown, unknown]) => moduleGetterContext(args, groupModule);
const actionContext = (context: ActionContext<unknown, unknown>) => localActionContext(context, groupModule);
